'use client';

/**
 * YarnSwatch — visual representation of a yarn's colorway.
 *
 * Two rendering modes, decided per-yarn:
 *   - If `colorPhoto` is set, render the photo. Lets users capture the
 *     real look of tweed / dyed / sock / gradient colorways via a
 *     close-up photo instead of trying to express them as hex values.
 *   - Otherwise, render a flat swatch using `colorHex`.
 *
 * The swatch sizes itself from the caller's `className` (e.g.
 * `w-12 h-12 rounded-xl`), so one component works for every display
 * site — yarn list cards, project card chips, inspiration cards, etc.
 */

import type { Yarn } from '@/lib/types';
import BlobImage from './BlobImage';

interface YarnSwatchProps {
  yarn: Pick<Yarn, 'colorHex' | 'colorPhoto'>;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  /**
   * Defer creating the photo's ObjectURL until the swatch is in (or near)
   * the viewport. Pass `lazy` on long lists so the initial mount doesn't
   * spike memory creating dozens of Blob ObjectURLs at once — what
   * crashed Veronika's iPhone on /yarn with 34 yarns in build 1003.
   */
  lazy?: boolean;
}

const FALLBACK = '#E8E4DF';

export default function YarnSwatch({ yarn, className, style, ariaLabel, lazy }: YarnSwatchProps) {
  if (yarn.colorPhoto) {
    return (
      <BlobImage
        src={yarn.colorPhoto}
        alt={ariaLabel}
        className={`${className ?? ''} object-cover`}
        style={style}
        lazy={lazy}
      />
    );
  }
  return (
    <div
      className={className}
      role="img"
      aria-label={ariaLabel}
      style={{ backgroundColor: yarn.colorHex || FALLBACK, ...style }}
    />
  );
}
